import {
    GiftCardBatchUploadResult,
    PointsAdjustmentRequest,
    PointsLedgerEntry,
    Redemption,
    RedemptionStatus,
    RedemptionTransitionRequest,
    RewardCatalogItem,
    RewardCatalogItemRequest,
    RewardsApi,
    RewardsConfiguration,
    RewardsStats
} from '@root/api';
import { translateAxiosError, TranslatedAxiosError } from '../services/migrated/axiosWrapper';

type RewardsCallback<T = unknown> = (err: TranslatedAxiosError | null, result?: T) => void;

const rewardsApi = new RewardsApi();

// ─── Configuration (DAV-20) ──────────────────────────────────────────────────

export const getRewardsConfig = (callback: RewardsCallback<RewardsConfiguration>): void => {
    rewardsApi.getRewardsConfig()
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const updateRewardsConfig = (
    configuration: RewardsConfiguration,
    callback: RewardsCallback<RewardsConfiguration>
): void => {
    rewardsApi.updateRewardsConfig(configuration)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

// ─── Catalog (DAV-21) ────────────────────────────────────────────────────────

export const listCatalog = (
    includeArchived: boolean,
    callback: RewardsCallback<RewardCatalogItem[]>
): void => {
    rewardsApi.listRewardsCatalog(includeArchived)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const createCatalogItem = (
    item: RewardCatalogItemRequest,
    callback: RewardsCallback<RewardCatalogItem>
): void => {
    rewardsApi.createRewardsCatalogItem(item)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const updateCatalogItem = (
    itemId: string,
    item: RewardCatalogItemRequest,
    callback: RewardsCallback<RewardCatalogItem>
): void => {
    rewardsApi.updateRewardsCatalogItem(itemId, item)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const deleteCatalogItem = (itemId: string, callback: RewardsCallback<void>): void => {
    rewardsApi.deleteRewardsCatalogItem(itemId)
        .then(() => callback(null))
        .catch((error) => callback(translateAxiosError(error)));
};

export const uploadCodes = (
    itemId: string,
    file: File,
    callback: RewardsCallback<GiftCardBatchUploadResult>
): void => {
    rewardsApi.uploadRewardsCodes(itemId, file)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

// ─── Redemptions (DAV-22) ────────────────────────────────────────────────────

export const listRedemptions = (
    status: RedemptionStatus | undefined,
    callback: RewardsCallback<Redemption[]>
): void => {
    rewardsApi.listRewardsRedemptions(status)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

/** Count of pending redemptions — used by the sidebar badge and the Canjes tab counter. */
export const getPendingRedemptionsCount = (
    callback: RewardsCallback<number>,
    options?: { backgroundPoll?: boolean }
): void => {
    rewardsApi.listRewardsRedemptions('PENDING' as RedemptionStatus, options)
        .then((response) => callback(null, (response.data ?? []).length))
        .catch((error) => callback(translateAxiosError(error)));
};

/**
 * Approve / deliver / reject / cancel a redemption. The optional body carries the
 * reject reason + message (reject) or the delivery note (deliver), persisted by
 * the backend and shown to the person in their history.
 */
export const transitionRedemption = (
    redemptionId: string,
    action: 'approve' | 'deliver' | 'reject' | 'cancel',
    callback: RewardsCallback<Redemption>,
    body?: RedemptionTransitionRequest
): void => {
    rewardsApi.transitionRewardsRedemption(redemptionId, action, body)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

// ─── Ledger & adjustments (DAV-22) ───────────────────────────────────────────

export const getLedger = (
    internalId: number,
    callback: RewardsCallback<PointsLedgerEntry[]>
): void => {
    rewardsApi.getRewardsLedger(internalId)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const adjustPoints = (
    request: PointsAdjustmentRequest,
    callback: RewardsCallback<void>
): void => {
    rewardsApi.adjustRewardsPoints(request)
        .then(() => callback(null))
        .catch((error) => callback(translateAxiosError(error)));
};

// ─── Analytics (DAV-23) ──────────────────────────────────────────────────────

export const getRewardsStats = (
    period: string,
    hierarchyId: string | undefined,
    callback: RewardsCallback<RewardsStats>
): void => {
    rewardsApi.getRewardsStats(period, hierarchyId)
        .then((response) => callback(null, response.data))
        .catch((error) => callback(translateAxiosError(error)));
};

export const rewardsService = {
    getRewardsConfig,
    updateRewardsConfig,
    listCatalog,
    createCatalogItem,
    updateCatalogItem,
    deleteCatalogItem,
    uploadCodes,
    listRedemptions,
    getPendingRedemptionsCount,
    transitionRedemption,
    getLedger,
    adjustPoints,
    getRewardsStats
};
